import React from 'react';
import { motion } from 'framer-motion';
import { Scissors } from 'lucide-react';

interface Service {
  id: string;
  name_he: string;
  description?: string;
  image_url?: string;
  price?: number;
  duration?: string;
}

interface ServicesShowcaseProps {
  services: Service[];
}

export function ServicesShowcase({ services }: ServicesShowcaseProps) {

  if (!services || services.length === 0) {

    return null;
  }

  return (
    <section className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            השירותים שלנו
          </h2>
          <p className="text-gray-600">
            גלה את מגוון השירותים המקצועיים שאנו מציעים
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
            >
              {/* Image */}
              <div className="relative h-48 bg-gradient-to-br from-blue-50 to-blue-100 overflow-hidden">
                {service.image_url ? (
                  <>
                    <img
                      src={service.image_url}
                      alt={service.name_he}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Scissors className="h-16 w-16 text-blue-300 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5">
                {/* Service Name */}
                <h4 className="text-lg font-bold text-gray-900 mb-2 text-center">
                  {service.name_he}
                </h4>

                {/* Description */}
                {service.description && (
                  <p className="text-gray-600 text-sm text-center line-clamp-3">
                    {service.description}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            מוכנים להתחיל?
          </h3>
          <p className="text-gray-600 mb-4">
            התחברו כדי לקבוע תור ולראות את המחירים המלאים
          </p>
          <button
            onClick={() => {
              const loginBtn = document.querySelector('[data-login-trigger]');
              if (loginBtn instanceof HTMLElement) {
                loginBtn.click();
              }
            }}
            className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            התחבר עכשיו
          </button>
        </motion.div>
      </div>
    </section>
  );
}
